/** 埋点追踪器的基础配置接口
 * 定义了初始化追踪器所需的各种参数
 */
export interface TrackerConfig {

  /** 单条事件上报路径（可选，使用默认配置） */
  event_path?: string;
  /** 批量事件上报路径（可选，使用默认配置） */
  batch_event_path?: string;
  /** 项目ID，必填项 */
  project_id: string;
  /** 数据上报的服务器地址（可选，使用默认配置） */
  server_url?: string;
  /** 是否启用自动埋点功能，可选 */
  enable_auto_track?: boolean;
  /** 是否开启调试模式，开启后会输出日志，可选 */
  debug?: boolean;
}

/** 事件类型枚举 */
export enum EventType {
  EVENT = "event", // 普通事件
  ERROR = "error", // 错误事件
}

/**
 * 事件键枚举
 * 定义系统支持的所有事件键，严格控制可上报的事件类型
 */
export enum EventKey {
  // 页面相关事件
  PAGE_VIEW = 'page_view', // 页面访问
  PAGE_LEAVE = 'page_leave', // 页面离开
  PAGE_VISIBILITY_CHANGE = 'page_visibility_change', // 页面可见性变化

  // 导航相关事件
  ROUTE_CHANGE = 'route_change', // 路由变化

  // 用户交互事件
  BUTTON_CLICK = 'button_click', // 按钮点击
  HOVER = 'hover', // 鼠标悬停
  DOUBLE_CLICK = 'double_click', // 双击
  RIGHT_CLICK = 'right_click', // 右键点击
  DRAG = 'drag', // 拖动
  DROP = 'drop', // 拖放
  SCROLL = 'scroll', // 滚动
  RESIZE = 'resize', // 调整大小
  FOCUS = 'focus', // 聚焦
  BLUR = 'blur', // 失焦
  USER_INPUT = 'user_input', // 用户输入
  CUSTOM_INTERACTION = 'custom_interaction', // 自定义交互
  FEEDBACK_SUBMIT = 'feedback_submit', // 反馈提交

  // 组件相关事件
  COMPONENT_LIFECYCLE = 'component_lifecycle', // 组件生命周期
  COMPONENT_ERROR = 'component_error', // 组件错误

  // 错误相关事件
  JAVASCRIPT_ERROR = 'javascript_error', // JavaScript错误
  RESOURCE_ERROR = 'resource_error', // 资源加载错误
  NETWORK_ERROR = 'network_error', // 网络错误
  CUSTOM_ERROR = 'custom_error', // 自定义错误

  // 业务相关事件
  USER_LOGIN = 'user_login', // 用户登录
  USER_LOGOUT = 'user_logout', // 用户登出
}

/**
 * 事件键到事件类型的映射
 * 定义每个事件键对应的默认事件类型
 */
export const EVENT_KEY_TYPE_MAP: Record<EventKey, EventType> = {
  // 页面相关事件 - 普通事件
  [EventKey.PAGE_VIEW]: EventType.EVENT,
  [EventKey.PAGE_LEAVE]: EventType.EVENT,
  [EventKey.PAGE_VISIBILITY_CHANGE]: EventType.EVENT,

  // 导航相关事件 - 普通事件
  [EventKey.ROUTE_CHANGE]: EventType.EVENT,

  // 用户交互事件 - 普通事件
  [EventKey.BUTTON_CLICK]: EventType.EVENT,
  [EventKey.HOVER]: EventType.EVENT,
  [EventKey.DOUBLE_CLICK]: EventType.EVENT,
  [EventKey.RIGHT_CLICK]: EventType.EVENT,
  [EventKey.DRAG]: EventType.EVENT,
  [EventKey.DROP]: EventType.EVENT,
  [EventKey.SCROLL]: EventType.EVENT,
  [EventKey.RESIZE]: EventType.EVENT,
  [EventKey.FOCUS]: EventType.EVENT,
  [EventKey.BLUR]: EventType.EVENT,
  [EventKey.USER_INPUT]: EventType.EVENT,
  [EventKey.CUSTOM_INTERACTION]: EventType.EVENT,
  [EventKey.FEEDBACK_SUBMIT]: EventType.EVENT,

  // 组件相关事件
  [EventKey.COMPONENT_LIFECYCLE]: EventType.EVENT,
  [EventKey.COMPONENT_ERROR]: EventType.ERROR,

  // 错误相关事件 - 错误事件
  [EventKey.JAVASCRIPT_ERROR]: EventType.ERROR,
  [EventKey.RESOURCE_ERROR]: EventType.ERROR,
  [EventKey.NETWORK_ERROR]: EventType.ERROR,
  [EventKey.CUSTOM_ERROR]: EventType.ERROR,

  // 业务相关事件 - 普通事件
  [EventKey.USER_LOGIN]: EventType.EVENT,
  [EventKey.USER_LOGOUT]: EventType.EVENT,
};

/**
 * 验证事件键和事件类型的组合是否有效
 * @param eventKey 事件键
 * @param eventType 事件类型
 * @returns 是否有效
 */
export function validateEventKeyType(eventKey: EventKey, eventType: EventType): boolean {
  return EVENT_KEY_TYPE_MAP[eventKey] === eventType;
}

/**
 * 获取事件键的默认事件类型
 * @param eventKey 事件键
 * @returns 默认事件类型
 */
export function getDefaultEventType(eventKey: EventKey): EventType {
  return EVENT_KEY_TYPE_MAP[eventKey];
}

/** 埋点事件接口
 * 定义了一个埋点事件的完整结构
 */
export interface TrackEvent {
  /** 项目ID */
  project_id: string;
  /** 事件标识符 */
  event_key: EventKey;
  /** 事件类型 */
  event_type: EventType;
  /** 事件发生时间 */
  event_time: Date;
  /** 会话ID */
  session_id: string;
  /** 设备ID */
  device_id?: string;
  /** 设备UA信息 */
  device_ua?: string;
  /** IP地址 */
  ip_address?: string;
  /** 页面URL */
  page_url?: string;
  /** 来源URL */
  referrer?: string;
  /** 元数据 */
  metadata?: Record<string, any>;
}
